/**
 * MarginNotePanel — dockable social annotation panel (social_annotation capability).
 *
 * A slide-in panel similar to NotesDockPanel but for social annotations.
 * Shows public annotations for the current scene grouped by type, lets the
 * learner post their own annotations, and supports upvoting public ones.
 *
 * Annotation types:
 *   confused   → orange
 *   insight    → blue
 *   question   → purple
 *   highlight  → yellow
 *
 * Props:
 *   sceneId            — string, current scene/segment ID
 *   domain             — string, current domain slug
 *   isOpen             — boolean, controls panel visibility
 *   onClose            — () => void
 *   currentTimestampSec — number | null, current video position in seconds
 *   currentSlideIndex  — number | null, current slide index (0-based)
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';

// ─── Constants ────────────────────────────────────────────────────────────────

const TYPE_META = {
  confused:  { label: 'Confused',  color: '#f97316', bg: '#431407', icon: '😕' },
  insight:   { label: 'Insight',   color: '#3b82f6', bg: '#1e3a5f', icon: '💡' },
  question:  { label: 'Question',  color: '#a855f7', bg: '#2e1065', icon: '❓' },
  highlight: { label: 'Highlight', color: '#eab308', bg: '#422006', icon: '✨' },
};

const ANCHOR_TYPES = ['timestamp', 'slide_index', 'text_selection', 'paragraph'];

const API_BASE = '/scenario-os/curriculum/annotations';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatTimestamp(sec) {
  if (sec == null) return null;
  const m = Math.floor(sec / 60);
  const s = String(Math.floor(sec % 60)).padStart(2, '0');
  return `${m}:${s}`;
}

function getAuthToken() {
  return (
    localStorage.getItem('scenario_os_token') ||
    localStorage.getItem('teachiq_token') ||
    sessionStorage.getItem('scenario_os_token') ||
    ''
  );
}

async function apiFetch(path, options = {}) {
  const token = getAuthToken();
  const resp = await fetch(path, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers || {}),
    },
  });
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({ detail: resp.statusText }));
    throw new Error(err?.detail || `HTTP ${resp.status}`);
  }
  if (resp.status === 204) return null;
  return resp.json();
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function TypeBadge({ type }) {
  const meta = TYPE_META[type] || { label: type, color: '#94a3b8', bg: '#1e293b', icon: '•' };
  return (
    <span style={{
      fontSize: 10,
      fontWeight: 700,
      color: meta.color,
      background: meta.bg,
      borderRadius: 4,
      padding: '1px 6px',
      letterSpacing: 0.5,
      textTransform: 'uppercase',
    }}>
      {meta.icon} {meta.label}
    </span>
  );
}

function AnnotationCard({ annotation, onUpvote, currentLearnerId }) {
  const isOwn = annotation.learner_id === currentLearnerId;
  const meta = TYPE_META[annotation.annotation_type] || {};
  const anchorLabel =
    annotation.timestamp_ms != null
      ? `📍 ${formatTimestamp(annotation.timestamp_ms / 1000)}`
      : annotation.slide_index != null
        ? `Slide ${annotation.slide_index + 1}`
        : annotation.selected_text
          ? `"${annotation.selected_text.slice(0, 40)}…"`
          : null;

  return (
    <div style={{
      background: '#1e293b',
      border: `1px solid ${annotation.is_public ? (meta.color || '#334155') : '#334155'}`,
      borderRadius: 8,
      padding: '10px 12px',
      marginBottom: 8,
      opacity: annotation.is_public || isOwn ? 1 : 0.7,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
        <TypeBadge type={annotation.annotation_type} />
        {annotation.is_public && (
          <button
            onClick={() => onUpvote(annotation.annotation_id)}
            title="Upvote"
            style={{
              background: 'none',
              border: '1px solid #334155',
              color: annotation.upvotes > 0 ? '#f59e0b' : '#64748b',
              borderRadius: 4,
              padding: '1px 6px',
              fontSize: 11,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 3,
            }}
          >
            ▲ {annotation.upvotes}
          </button>
        )}
      </div>
      {anchorLabel && (
        <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 4 }}>{anchorLabel}</div>
      )}
      <div style={{ fontSize: 13, color: '#cbd5e1', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
        {annotation.annotation_text}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 6 }}>
        <span style={{ fontSize: 10, color: '#475569' }}>
          {isOwn ? 'You' : 'Community'} · {annotation.is_public ? 'Public' : 'Private'}
        </span>
      </div>
    </div>
  );
}

function SectionHeader({ type, count }) {
  const meta = TYPE_META[type] || { label: type, color: '#94a3b8', icon: '•' };
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: 6,
      marginTop: 12,
      marginBottom: 6,
      paddingBottom: 4,
      borderBottom: `1px solid ${meta.color}44`,
    }}>
      <span style={{ color: meta.color, fontSize: 14 }}>{meta.icon}</span>
      <span style={{ fontSize: 12, fontWeight: 700, color: meta.color, textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {meta.label}
      </span>
      <span style={{
        background: meta.color,
        color: '#fff',
        borderRadius: 999,
        fontSize: 10,
        fontWeight: 700,
        padding: '0 5px',
      }}>
        {count}
      </span>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function MarginNotePanel({
  sceneId,
  domain,
  isOpen,
  onClose,
  currentTimestampSec = null,
  currentSlideIndex = null,
}) {
  const [annotations, setAnnotations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Form state
  const [annotationText, setAnnotationText] = useState('');
  const [annotationType, setAnnotationType] = useState('question');
  const [anchorType, setAnchorType] = useState('timestamp');
  const [isPublic, setIsPublic] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  // Current learner (from JWT)
  const [currentLearnerId, setCurrentLearnerId] = useState(null);
  useEffect(() => {
    try {
      const token = getAuthToken();
      if (token) {
        const payload = JSON.parse(atob(token.split('.')[1]));
        setCurrentLearnerId(payload.sub || payload.learner_id || null);
      }
    } catch {
      setCurrentLearnerId(null);
    }
  }, []);

  // Load annotations when panel opens or scene changes
  const fetchAnnotations = useCallback(async () => {
    if (!sceneId) return;
    setLoading(true);
    setError('');
    try {
      const data = await apiFetch(
        `${API_BASE}?scene_id=${encodeURIComponent(sceneId)}&include_public=true&limit=100`,
      );
      setAnnotations(data?.annotations || []);
    } catch (e) {
      setError(e.message || 'Failed to load annotations.');
    } finally {
      setLoading(false);
    }
  }, [sceneId]);

  useEffect(() => {
    if (isOpen) {
      fetchAnnotations();
      setAnnotationText('');
      setFormError('');
    }
  }, [isOpen, sceneId, fetchAnnotations]);

  // Esc to close
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  const handleSubmit = useCallback(async () => {
    if (!annotationText.trim()) return;
    setSaving(true);
    setFormError('');
    try {
      const body = {
        scene_id: sceneId,
        domain,
        anchor_type: anchorType,
        annotation_text: annotationText.trim(),
        annotation_type: annotationType,
        is_public: isPublic,
      };
      if (anchorType === 'timestamp' && currentTimestampSec != null) {
        body.timestamp_ms = Math.round(currentTimestampSec * 1000);
      }
      if (anchorType === 'slide_index' && currentSlideIndex != null) {
        body.slide_index = currentSlideIndex;
      }

      const created = await apiFetch(API_BASE, {
        method: 'POST',
        body: JSON.stringify(body),
      });
      setAnnotations((prev) => [created, ...prev]);
      setAnnotationText('');
    } catch (e) {
      setFormError(e.message || 'Failed to save annotation.');
    } finally {
      setSaving(false);
    }
  }, [annotationText, sceneId, domain, anchorType, annotationType, isPublic, currentTimestampSec, currentSlideIndex]);

  const handleUpvote = useCallback(async (annotationId) => {
    try {
      const updated = await apiFetch(`${API_BASE}/${annotationId}/upvote`, { method: 'POST' });
      setAnnotations((prev) =>
        prev.map((a) => (a.annotation_id === annotationId ? updated : a)),
      );
    } catch (e) {
      // Silently ignore upvote errors
    }
  }, []);

  if (!isOpen) return null;

  // Group public annotations by type for display
  const publicAnnotations = annotations.filter((a) => a.is_public || a.learner_id === currentLearnerId);
  const grouped = {};
  for (const type of Object.keys(TYPE_META)) {
    const items = publicAnnotations.filter((a) => a.annotation_type === type);
    if (items.length > 0) grouped[type] = items;
  }

  const totalCount = publicAnnotations.length;

  const anchorPreview =
    anchorType === 'timestamp' && currentTimestampSec != null
      ? `📍 ${formatTimestamp(currentTimestampSec)}`
      : anchorType === 'slide_index' && currentSlideIndex != null
        ? `Slide ${currentSlideIndex + 1}`
        : null;

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed', inset: 0,
          background: 'rgba(0,0,0,0.4)',
          zIndex: 300,
        }}
        aria-hidden="true"
      />

      {/* Panel */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Social annotations panel"
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          width: 380,
          background: '#0f172a',
          color: '#e2e8f0',
          borderLeft: '1px solid #1e293b',
          boxShadow: '-4px 0 24px rgba(0,0,0,0.5)',
          zIndex: 301,
          display: 'flex',
          flexDirection: 'column',
          fontFamily: 'system-ui, -apple-system, sans-serif',
          animation: 'slideInRight 0.2s ease-out',
        }}
      >
        {/* Header */}
        <div style={{
          padding: '14px 16px',
          borderBottom: '1px solid #1e293b',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexShrink: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 18 }}>💬</span>
            <span style={{ fontWeight: 700, fontSize: 15 }}>Annotations</span>
            {totalCount > 0 && (
              <span style={{
                background: '#a855f7',
                color: '#fff',
                borderRadius: 999,
                fontSize: 11,
                fontWeight: 700,
                padding: '1px 7px',
              }}>
                {totalCount}
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            aria-label="Close annotations panel"
            style={{
              background: 'none', border: 'none',
              color: '#64748b', fontSize: 20,
              cursor: 'pointer', padding: 4, lineHeight: 1,
            }}
          >
            ✕
          </button>
        </div>

        {/* Add annotation form */}
        <div style={{
          padding: '12px 14px',
          borderBottom: '1px solid #1e293b',
          flexShrink: 0,
        }}>
          {/* Annotation type picker */}
          <div style={{ display: 'flex', gap: 6, marginBottom: 8, flexWrap: 'wrap' }}>
            {Object.entries(TYPE_META).map(([type, meta]) => (
              <button
                key={type}
                onClick={() => setAnnotationType(type)}
                style={{
                  padding: '3px 9px',
                  borderRadius: 6,
                  border: `1px solid ${annotationType === type ? meta.color : '#334155'}`,
                  background: annotationType === type ? meta.bg : 'transparent',
                  color: annotationType === type ? meta.color : '#64748b',
                  fontSize: 11,
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                }}
              >
                {meta.icon} {meta.label}
              </button>
            ))}
          </div>

          {/* Anchor type selector */}
          <div style={{ display: 'flex', gap: 6, marginBottom: 8, alignItems: 'center' }}>
            <span style={{ fontSize: 11, color: '#64748b', flexShrink: 0 }}>Anchor:</span>
            <select
              value={anchorType}
              onChange={(e) => setAnchorType(e.target.value)}
              style={{
                background: '#1e293b',
                border: '1px solid #334155',
                borderRadius: 5,
                color: '#94a3b8',
                fontSize: 11,
                padding: '2px 6px',
                cursor: 'pointer',
              }}
            >
              <option value="timestamp">Video timestamp</option>
              <option value="slide_index">Slide</option>
              <option value="text_selection">Text selection</option>
              <option value="paragraph">Paragraph</option>
            </select>
            {anchorPreview && (
              <span style={{ fontSize: 11, color: '#f59e0b', fontWeight: 600 }}>
                {anchorPreview}
              </span>
            )}
          </div>

          {/* Text area */}
          <textarea
            value={annotationText}
            onChange={(e) => setAnnotationText(e.target.value)}
            placeholder={`Add a ${annotationType}…`}
            rows={3}
            style={{
              width: '100%',
              background: '#1e293b',
              border: `1px solid ${TYPE_META[annotationType]?.color || '#334155'}55`,
              borderRadius: 7,
              color: '#e2e8f0',
              fontSize: 13,
              padding: '8px 10px',
              resize: 'vertical',
              fontFamily: 'inherit',
              lineHeight: 1.5,
              boxSizing: 'border-box',
              outline: 'none',
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) handleSubmit();
            }}
          />

          {formError && (
            <div style={{ fontSize: 12, color: '#f87171', marginTop: 4 }}>{formError}</div>
          )}

          {/* Public toggle + submit */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
            <label style={{
              display: 'flex', alignItems: 'center', gap: 6,
              fontSize: 12, color: '#94a3b8', cursor: 'pointer',
            }}>
              <input
                type="checkbox"
                checked={isPublic}
                onChange={(e) => setIsPublic(e.target.checked)}
                style={{ accentColor: '#a855f7' }}
              />
              Make public
            </label>
            <button
              onClick={handleSubmit}
              disabled={!annotationText.trim() || saving}
              style={{
                background: annotationText.trim() ? (TYPE_META[annotationType]?.color || '#a855f7') : '#1e293b',
                color: annotationText.trim() ? '#fff' : '#475569',
                border: '1px solid #334155',
                borderRadius: 6,
                padding: '5px 14px',
                fontSize: 12,
                fontWeight: 600,
                cursor: annotationText.trim() ? 'pointer' : 'default',
                transition: 'background 0.15s',
              }}
            >
              {saving ? 'Posting…' : 'Post (Ctrl+↵)'}
            </button>
          </div>
        </div>

        {/* Annotations list */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '10px 14px' }}>
          {loading && (
            <div style={{ color: '#475569', fontSize: 13, textAlign: 'center', padding: '20px 0' }}>
              Loading…
            </div>
          )}
          {!loading && error && (
            <div style={{ color: '#f87171', fontSize: 12, padding: '12px 0' }}>{error}</div>
          )}
          {!loading && !error && totalCount === 0 && (
            <div style={{
              color: '#475569', fontSize: 13,
              textAlign: 'center', padding: '32px 0', lineHeight: 1.6,
            }}>
              <div style={{ fontSize: 28, marginBottom: 8, opacity: 0.4 }}>💬</div>
              No annotations yet for this scene.
              <br />
              Be the first to add one above.
            </div>
          )}
          {!loading && !error && Object.entries(grouped).map(([type, items]) => (
            <div key={type}>
              <SectionHeader type={type} count={items.length} />
              {items.map((ann) => (
                <AnnotationCard
                  key={ann.annotation_id}
                  annotation={ann}
                  onUpvote={handleUpvote}
                  currentLearnerId={currentLearnerId}
                />
              ))}
            </div>
          ))}
        </div>
      </div>
    </>
  );
}
